// The fund-once workbench action bar — Story #5305 Stream 14b (Task #5318),
// design §6.4, replacing GroupDetail.tsx's own disabled-placeholder card
// (data-testid="overlap-workbench-placeholder", "Coming with the fund-once
// workbench"). Mockup: documentation/SecureEmbed_WhiteSpace_Refinement_
// Overlap-assets/screenB-overlap.html's `.wb` card.
//
// Three actions, three different floors:
//   - Draft consolidated funding case (.docx): "available to any active
//     member, audited" (§6.4) — no role gate here.
//   - Find funding bodies: a Directory handoff, no role gate.
//   - Raise with the N project leads: admin/billing-owner only, because
//     "it is the least reversible, most outward act... and send-once makes
//     a wrong send unrecoverable" (§6.4) — disabled-with-reason below the
//     floor, per this task's own UI acceptance note, never hidden (a hidden
//     button would look like the feature doesn't exist at all).
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileDown, Loader2, Mail, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getOverlapGroupBrief } from '../../services/api'
import { downloadBase64File } from './PortfolioWatchPanel'
import { OVERLAP_STALE_GROUP_MESSAGE } from '../../lib/whitespace'
import type { AggregatorRole } from '../../types'
import OverlapNotifyLeadsModal from './OverlapNotifyLeadsModal'

const NOTIFY_ROLES: AggregatorRole[] = ['admin', 'billing_owner']

interface OverlapWorkbenchProps {
  groupId: string
  aggregatorId: string | null
  memberCount: number
  /** null while the caller's role hasn't loaded yet — "Raise with the
   * leads" stays disabled (never optimistically enabled) until it has. */
  callerRole: AggregatorRole | null
}

export default function OverlapWorkbench({ groupId, aggregatorId, memberCount, callerRole }: OverlapWorkbenchProps) {
  const navigate = useNavigate()
  const [ask, setAsk] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [notifyModalOpen, setNotifyModalOpen] = useState(false)

  const briefMutation = useMutation({
    mutationFn: () => getOverlapGroupBrief(groupId, ask || undefined, aggregatorId),
    onSuccess: (brief) => {
      downloadBase64File(brief.docx_base64, brief.filename, brief.content_type)
      setNotice(`Downloaded ${brief.filename}.`)
    },
    onError: (e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      setNotice(status === 404 ? OVERLAP_STALE_GROUP_MESSAGE : (e as Error)?.message ?? 'Failed to generate the funding case.')
    },
  })

  const canNotify = callerRole != null && NOTIFY_ROLES.includes(callerRole)

  return (
    <div className="card p-3" data-testid="overlap-workbench">
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-xs text-iflow-900">Fund once, not {memberCount} times:</b>
        <button
          type="button"
          onClick={() => briefMutation.mutate()}
          disabled={briefMutation.isPending}
          data-testid="overlap-draft-brief-button"
          className="btn pri text-[11px] flex items-center gap-1.5 disabled:opacity-50"
        >
          {briefMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          {briefMutation.isPending ? 'Drafting…' : 'Draft consolidated funding case (.docx)'}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/ecosystem?type=funding_body&from=overlap-group-${groupId}`)}
          data-testid="overlap-find-funding-bodies-button"
          className="btn text-[11px] flex items-center gap-1.5"
        >
          <Search className="w-3.5 h-3.5" /> Find funding bodies
        </button>
        <button
          type="button"
          onClick={() => setNotifyModalOpen(true)}
          disabled={callerRole == null || !canNotify}
          title={callerRole != null && !canNotify ? 'Only Admin or Billing Owner can raise this group with its leads' : undefined}
          data-testid="overlap-raise-with-leads-button"
          className="btn text-[11px] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Mail className="w-3.5 h-3.5" /> Raise with the {memberCount} project leads
        </button>
        <span className="text-[10.5px] text-gray-500">
          Leads are asked privately — nothing about the other projects is shared with them.
        </span>
      </div>

      <div className="mt-2.5">
        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide" htmlFor={`overlap-ask-${groupId}`}>
          Consolidated ask (optional — included in the funding case)
        </label>
        <input
          id={`overlap-ask-${groupId}`}
          type="text"
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          placeholder="e.g. $250k to consolidate marine acoustic sensing across the three projects"
          className="w-full text-[11.5px] border border-sky-200 rounded-lg px-2 py-1.5 mt-1"
          data-testid="overlap-ask-input"
        />
      </div>

      {notice && (
        <p className="text-[10.5px] text-gray-600 mt-2" role="status" data-testid="overlap-workbench-notice">
          {notice}
        </p>
      )}

      {notifyModalOpen && (
        <OverlapNotifyLeadsModal
          groupId={groupId}
          aggregatorId={aggregatorId}
          memberCount={memberCount}
          onClose={() => setNotifyModalOpen(false)}
        />
      )}
    </div>
  )
}
